import { supabase, Miembro, InvitationLog } from '../lib/supabase';
import { FK } from '../lib/foreignKeys';

export interface MiembroFilters {
  busqueda?: string;
  tipoMiembro?: 'adulto' | 'niño';
  misioneroNacional?: boolean;
  instructorLocal?: boolean;
  cargo?: string;
  estadoFicha?: string;
}

export interface PaginatedMiembrosResponse {
  data: Miembro[];
  total: number;
  totalAdultos: number;
  totalNinos: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MiembroSearchResult {
  id: string;
  nombres: string;
  apellidos: string;
  rut_pasaporte?: string;
  aporte_mensual?: number;
  aporte_cuota_iglesia?: number;
  lumisial_actual_id?: string;
  modo_pago_cuota_iglesia?: string | null;
}

export const miembroService = {
  async getByLumisial(lumisialId: string): Promise<Miembro[]> {
    const { data, error } = await supabase
      .from('miembros')
      .select('*')
      .eq('lumisial_actual_id', lumisialId)
      .order('nombres', { ascending: true })
      .order('apellidos', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getPaginated(
    lumisialId: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: MiembroFilters
  ): Promise<PaginatedMiembrosResponse> {
    // Búsqueda paginada con soporte de unaccent (ignora acentos)
    const rpcParams: Record<string, string | number | boolean> = {
      p_lumisial_id: lumisialId,
      p_page: page,
      p_page_size: pageSize
    };

    const countParams: Record<string, string | number | boolean> = {
      p_lumisial_id: lumisialId
    };

    if (filters?.busqueda?.trim()) {
      rpcParams.p_search_term = filters.busqueda.trim();
      countParams.p_search_term = filters.busqueda.trim();
    }
    if (filters?.tipoMiembro) {
      rpcParams.p_tipo_miembro = filters.tipoMiembro;
      countParams.p_tipo_miembro = filters.tipoMiembro;
    }
    if (filters?.misioneroNacional !== undefined) {
      rpcParams.p_misionero_nacional = filters.misioneroNacional;
      countParams.p_misionero_nacional = filters.misioneroNacional;
    }
    if (filters?.instructorLocal !== undefined) {
      rpcParams.p_instructor_local = filters.instructorLocal;
      countParams.p_instructor_local = filters.instructorLocal;
    }
    if (filters?.cargo) {
      rpcParams.p_cargo = filters.cargo;
      countParams.p_cargo = filters.cargo;
    }
    if (filters?.estadoFicha) {
      rpcParams.p_estado_ficha = filters.estadoFicha;
      countParams.p_estado_ficha = filters.estadoFicha;
    }

    const [{ data, error }, { data: countData, error: countError }] = await Promise.all([
      supabase.rpc('buscar_miembros_paginado', rpcParams),
      supabase.rpc('contar_miembros_filtrados', countParams)
    ]);

    if (error) throw error;
    if (countError) throw countError;

    const counts = countData?.[0] || { total: 0, total_adultos: 0, total_ninos: 0 };
    const total = Number(counts.total) || 0;
    const totalAdultos = Number(counts.total_adultos) || 0;
    const totalNinos = Number(counts.total_ninos) || 0;
    const totalPages = Math.ceil(total / pageSize);

    return {
      data: (data || []) as Miembro[],
      total,
      totalAdultos,
      totalNinos,
      page,
      pageSize,
      totalPages
    };
  },

  async getById(id: string): Promise<Miembro | null> {
    const { data, error } = await supabase
      .from('miembros')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getByIdConJerarquia(id: string): Promise<Miembro | null> {
    const { data, error } = await supabase
      .from('miembros')
      .select(`
        *,
        lumisial:lumisiales!lumisial_actual_id(
          id, nombre, diocesis_id, provincia_eclesiastica_id,
          diocesis:diocesis!${FK.LUMISIALES_DIOCESIS}(id, nombre, tipo),
          provincia_eclesiastica:provincias_eclesiasticas!${FK.LUMISIALES_PROVINCIA}(id, nombre)
        )
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getByIds(ids: string[]): Promise<Miembro[]> {
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from('miembros')
      .select('*')
      .in('id', ids);

    if (error) throw error;
    return data || [];
  },

  async searchByLumisial(lumisialId: string, searchTerm: string, limit: number = 50): Promise<MiembroSearchResult[]> {
    if (!searchTerm.trim()) {
      const { data, error } = await supabase
        .from('miembros')
        .select('id, nombres, apellidos, rut_pasaporte, aporte_mensual, aporte_cuota_iglesia, modo_pago_cuota_iglesia')
        .eq('lumisial_actual_id', lumisialId)
        .eq('estado_miembro', 'activo')
        .order('nombres', { ascending: true })
        .order('apellidos', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return (data || []) as MiembroSearchResult[];
    }

    // Búsqueda sin sensibilidad a acentos usando función RPC con unaccent()
    const { data, error } = await supabase
      .rpc('buscar_miembros_sin_acentos', {
        p_lumisial_id: lumisialId,
        p_search_term: searchTerm.trim(),
        p_limit: limit
      });

    if (error) throw error;
    return (data || []) as MiembroSearchResult[];
  },

  async searchByLumisialIds(lumisialIds: string[], searchTerm: string, limit = 50): Promise<MiembroSearchResult[]> {
    if (lumisialIds.length === 0) return [];

    let query = supabase
      .from('miembros')
      .select('id, nombres, apellidos, rut_pasaporte, aporte_mensual, aporte_cuota_iglesia, lumisial_actual_id, modo_pago_cuota_iglesia')
      .eq('estado_miembro', 'activo')
      .in('lumisial_actual_id', lumisialIds)
      .order('nombres', { ascending: true })
      .order('apellidos', { ascending: true })
      .limit(limit);

    if (searchTerm.trim()) {
      const term = `%${searchTerm.trim()}%`;
      query = query.or(`nombres.ilike.${term},apellidos.ilike.${term}`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as MiembroSearchResult[];
  },

  async searchByDiocesis(diocesisId: string, searchTerm: string, limit = 50): Promise<MiembroSearchResult[]> {
    const { data: lumisiales, error: lumisialesError } = await supabase
      .from('lumisiales').select('id').eq('diocesis_id', diocesisId).eq('activo', true);
    if (lumisialesError) throw lumisialesError;
    return this.searchByLumisialIds((lumisiales || []).map(l => l.id), searchTerm, limit);
  },

  async searchByProvincia(provinciaId: string, searchTerm: string, limit = 50): Promise<MiembroSearchResult[]> {
    const { data: lumisiales, error: lumisialesError } = await supabase
      .from('lumisiales').select('id').eq('provincia_eclesiastica_id', provinciaId).eq('activo', true);
    if (lumisialesError) throw lumisialesError;
    return this.searchByLumisialIds((lumisiales || []).map(l => l.id), searchTerm, limit);
  },

  async getCountByLumisial(lumisialId: string): Promise<number> {
    const { count, error } = await supabase
      .from('miembros')
      .select('*', { count: 'exact', head: true })
      .eq('lumisial_actual_id', lumisialId)
      .eq('estado_miembro', 'activo');

    if (error) throw error;
    return count || 0;
  },

  async create(miembro: Omit<Miembro, 'id' | 'created_at'>): Promise<Miembro> {
    const { data, error } = await supabase
      .from('miembros')
      .insert(miembro)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, miembro: Partial<Omit<Miembro, 'id' | 'created_at'>>): Promise<Miembro> {
    const { data, error } = await supabase
      .from('miembros')
      .update(miembro)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async softDelete(id: string, motivo: string, cambiadoPorId: string): Promise<void> {

    const miembro = await this.getById(id);
    if (!miembro) throw new Error('Miembro no encontrado');

    const { error } = await supabase.rpc('registrar_cambio_estado_miembro', {
      p_miembro_id: id,
      p_estado_anterior: miembro.estado_miembro,
      p_estado_nuevo: 'sin_derechos',
      p_motivo: motivo,
      p_cambiado_por_id: cambiadoPorId,
      p_numero_ingreso_nuevo: miembro.numero_ingreso
    });

    if (error) throw error;
  },

  async hardDelete(id: string): Promise<void> {
    const { error } = await supabase
      .from('miembros')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async delete(_id: string): Promise<void> {
    throw new Error('Use softDelete o hardDelete según el caso');
  },

  async search(searchTerm: string, limit: number = 10): Promise<Miembro[]> {
    const { data, error } = await supabase
      .from('miembros')
      .select(`
        *,
        lumisial:lumisiales!lumisial_actual_id(
          id, nombre, diocesis_id, provincia_eclesiastica_id,
          diocesis:diocesis!${FK.LUMISIALES_DIOCESIS}(id, nombre, tipo),
          provincia_eclesiastica:provincias_eclesiasticas!${FK.LUMISIALES_PROVINCIA}(id, nombre)
        )
      `)
      .or(`nombres.ilike.%${searchTerm}%,apellidos.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
      .order('nombres', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async getMiembrosSinDerechos(
    lumisialId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedMiembrosResponse> {
    const { data, error, count } = await supabase
      .from('miembros')
      .select('*', { count: 'exact' })
      .eq('lumisial_actual_id', lumisialId)
      .eq('estado_miembro', 'sin_derechos')
      .order('estado_cambiado_fecha', { ascending: false })
      .order('nombres', { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (error) throw error;

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    const { data: adultosData } = await supabase
      .from('miembros')
      .select('tipo_miembro', { count: 'exact' })
      .eq('lumisial_actual_id', lumisialId)
      .eq('estado_miembro', 'sin_derechos')
      .eq('tipo_miembro', 'adulto');

    const { data: ninosData } = await supabase
      .from('miembros')
      .select('tipo_miembro', { count: 'exact' })
      .eq('lumisial_actual_id', lumisialId)
      .eq('estado_miembro', 'sin_derechos')
      .eq('tipo_miembro', 'niño');

    const totalAdultos = adultosData?.length || 0;
    const totalNinos = ninosData?.length || 0;

    return {
      data: data || [],
      total,
      totalAdultos,
      totalNinos,
      page,
      pageSize,
      totalPages
    };
  },

  async reingresarMiembro(id: string, cambiadoPorId: string): Promise<void> {

    const miembro = await this.getById(id);
    if (!miembro) throw new Error('Miembro no encontrado');
    if (miembro.estado_miembro !== 'sin_derechos') {
      throw new Error('El miembro ya está activo');
    }

    const nuevoNumeroIngreso = miembro.numero_ingreso + 1;

    const { error } = await supabase.rpc('registrar_cambio_estado_miembro', {
      p_miembro_id: id,
      p_estado_anterior: 'sin_derechos',
      p_estado_nuevo: 'activo',
      p_motivo: `Reingreso número ${nuevoNumeroIngreso}`,
      p_cambiado_por_id: cambiadoPorId,
      p_numero_ingreso_nuevo: nuevoNumeroIngreso
    });

    if (error) throw error;
  },

  async getInvitationLogs(miembroId: string): Promise<InvitationLog[]> {
    const { data, error } = await supabase
      .from('invitation_logs')
      .select('*, generador:miembros!generated_by(nombres, apellidos)')
      .eq('miembro_id', miembroId)
      .order('generated_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as InvitationLog[];
  },

  async registrarMiembro(miembroId: string): Promise<void> {
    const { error } = await supabase
      .from('miembros')
      .update({ estado_ficha: 'registrado' })
      .eq('id', miembroId);
    if (error) throw error;

    const { error: rpcError } = await supabase.rpc('notificar_miembro_registrado', {
      p_miembro_id: miembroId,
    });
    if (rpcError) throw rpcError;
  },

  async devolverFicha(miembroId: string, observacion: string): Promise<void> {
    // `foto_compromiso_hasta` se limpia acá y también al generar un link nuevo, aunque devolver la
    // ficha dispare las dos cosas: son operaciones separadas y la segunda puede fallar por su tope
    // diario. Sin esta limpieza, una devolución que no alcanza a generar link deja al miembro
    // contado como foto pendiente para siempre, con un compromiso que ya no existe.
    const { error } = await supabase
      .from('miembros')
      .update({
        estado_ficha: 'sin_completar',
        comentario_devolucion_ficha: observacion,
        foto_compromiso_hasta: null,
      })
      .eq('id', miembroId);
    if (error) throw error;
  },

  async getEstadoFichaStats(lumisialId: string): Promise<{
    total_por_solicitar: number;
    total_solicitado: number;
    total_link_vencido: number;
    total_prerregistrado: number;
    total_registrado: number;
    total_foto_pendiente: number;
  }> {
    const { data, error } = await supabase
      .rpc('contar_estado_ficha', { p_lumisial_id: lumisialId });
    if (error) throw error;
    const row = (data as Array<Record<string, unknown>>)?.[0] ?? {};
    return {
      total_por_solicitar:  Number(row.total_por_solicitar)  || 0,
      total_solicitado:     Number(row.total_solicitado)     || 0,
      total_link_vencido:   Number(row.total_link_vencido)   || 0,
      total_prerregistrado: Number(row.total_prerregistrado) || 0,
      total_registrado:     Number(row.total_registrado)     || 0,
      total_foto_pendiente: Number(row.total_foto_pendiente) || 0,
    };
  },
};
